// ============================================================================
// Rotating encouragement pool — used as the opening hype line on tasks 4, 5,
// 6, 7, and 8 across all 12 template-based interventions.
//
// Guarantee: within a single intervention, no phrase ever repeats. The pool
// has 16 phrases and an intervention uses at most 5, so exhaustion is not a
// realistic concern; the fallback below keeps the picker safe if someone
// shrinks the pool or expands the templated-task set.
//
// Across interventions we additionally avoid immediately re-using the most
// recent phrases (rolling history) so back-to-back runs don't open with the
// same word.
// ============================================================================

import { secureRandom } from "./secureRandom";

export const ENCOURAGEMENTS = [
  // High-energy / big-win
  "Whoo hoo! You're cooking now!",
  "Look at you go! The momentum is real.",
  "Yes! That's the shift right there.",
  "Boom — now we're rolling!",
  "There it is! You're in the groove now.",
  "WOO HOO! You're hitting your stride.",
  "Oh, you're firing on all cylinders now!",
  "You just felt that shift, didn't you?",
  "That's it — the movement is real.",
  // Warm cheer
  "Super! Look how far you've come.",
  "Nicely done! Your brain is already working its magic.",
  "Beautiful! You're right where you need to be.",
  "Something's starting to lift — feel it?",
  "You're settling into it now.",
  // Grit-celebrating
  "You're giving an outstanding amount of effort!",
  "That's real courage you're showing!",
  "Look at that — you're doing the hard, brave thing.",
  "You're leaning in — and it shows!",
  "The bravery in this is not small.",
  // Gentler (for heavier interventions — grief, sadness, healing)
  "You've come such a long way today.",
  "Quietly brave work — and it's paying off.",
  "Steady, tender work — exactly what this asks for.",
  "This kind of showing up matters. And it's you doing it.",
];

// Per-intervention used set — every phrase picked during the current
// intervention lands here and is excluded from future draws until the next
// intervention resets it. This is the strong "no repeats within one
// intervention" guarantee.
let usedThisIntervention = new Set();

// Rolling cross-intervention history — prevents the same opener two sessions
// in a row. Only consulted when the per-intervention used set doesn't
// already exclude it.
let recentHistory = [];
const HISTORY_WINDOW = 4;

export function pickEncouragement() {
  // First choice: phrases not yet used THIS intervention and not in the
  // short cross-intervention history.
  let pool = ENCOURAGEMENTS.filter(
    (p) => !usedThisIntervention.has(p) && !recentHistory.includes(p)
  );
  // Fallback A: if the combined filter empties the pool (tiny pool or very
  // long intervention), relax the cross-intervention history.
  if (pool.length === 0) {
    pool = ENCOURAGEMENTS.filter((p) => !usedThisIntervention.has(p));
  }
  // Fallback B: every phrase has been used in this intervention — allow a
  // repeat, but at least avoid the most recent pick.
  if (pool.length === 0) {
    const lastPick = recentHistory[recentHistory.length - 1];
    pool = ENCOURAGEMENTS.filter((p) => p !== lastPick);
  }
  const chosen = pool[Math.floor(secureRandom() * pool.length)] || ENCOURAGEMENTS[0];
  usedThisIntervention.add(chosen);
  recentHistory = [...recentHistory, chosen].slice(-HISTORY_WINDOW);
  return chosen;
}

// Wipe the per-intervention used set at the start of a fresh intervention
// so the same 16-phrase pool is fully available again. The cross-
// intervention rolling history is intentionally left in place so two
// back-to-back intervention runs don't open with the same phrase.
export function resetEncouragementHistory() {
  usedThisIntervention = new Set();
}
